import { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { STATUS_CODES } from 'src/app/shared/constants/common.constants'
import { IAccount, ICreateAccount } from 'src/app/shared/models/accountSetup.model'
import { IClient } from 'src/app/shared/models/clientSetup.model'
import { getAccountById } from 'src/app/shared/services/AccountService'
import { getClientById } from 'src/app/shared/services/ClientSetupService'
import { ACCOUNT_STATUS, PROCESSING_TYPES, defaultEditValues } from '../constants'
import { ViewAccountFormValues } from '../models'
import { convertTime } from '../utils'
import { ViewAccountContext } from './index.context'

export type IDuplicatedAccounts = Array<
  Pick<ICreateAccount.Payload, 'accountName' | 'accountNumber'> & {
    isActive: boolean
  }
>

export type IToastType = 'new' | 'active' | 'archive' | 'update'

export type IActionParams = {
  accountName: string
  accountNumber: string
}

export const ViewAccountProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props
  const { t } = useTranslation()
  const { id } = useParams()
  const [loading, setLoading] = useState<boolean>(false)
  const [isFetched, setIsFetched] = useState<boolean>(false)
  const [client, setClient] = useState<IClient | null>(null)

  const form = useForm<ViewAccountFormValues>({
    defaultValues: defaultEditValues,
    mode: 'all',
  })

  const checkExceptionProcessingType = useWatch({
    control: form.control,
    name: 'checkExceptionProcessingType',
  })

  const achExceptionProcessingType = useWatch({
    control: form.control,
    name: 'achExceptionProcessingType',
  })

  const handleGetClient = useCallback(
    async (id: string) => {
      try {
        setLoading(true)
        const clientResponse = await getClientById({
          clientId: id,
        })
        if (clientResponse.statusCode === STATUS_CODES.SUCCESS) {
          setClient(clientResponse.data)
          form.clearErrors()
        }
      } catch (error) {
        console.log('ERROR: ', error)
      } finally {
        setLoading(false)
      }
    },
    [form],
  )

  const handleSetAccount = useCallback(
    (account: IAccount) => {
      Object.keys(account).forEach((prop: any) => {
        const value = (account as IAccount)[prop as keyof IAccount]
        if (prop === 'createdAt') {
          form.setValue(
            'createdAt',
            convertTime({
              date: value,
            }),
          )
        } else if (prop === 'status') {
          form.setValue('isArchiveAccount', account['status'] === ACCOUNT_STATUS.ARCHIVE)
        } else {
          form.setValue(prop as any, value)
        }
      })
    },
    [form],
  )

  useEffect(() => {
    if (checkExceptionProcessingType === PROCESSING_TYPES.NOCREATE) {
      form.setValue('checkDecision', '')
      form.setValue('checkReason', '')
      form.clearErrors('checkDecision')
      form.clearErrors('checkReason')
    }
  }, [checkExceptionProcessingType, form, form.setValue])

  useEffect(() => {
    if (achExceptionProcessingType === PROCESSING_TYPES.NOCREATE) {
      form.setValue('achDecision', '')
      form.setValue('achReason', '')
      form.clearErrors('achDecision')
      form.clearErrors('achReason')
    }
  }, [achExceptionProcessingType, form, form.setValue])

  // set service type parameter
  useEffect(() => {
    const isCreatingCheck = checkExceptionProcessingType === PROCESSING_TYPES.CREATE
    const isCreatingACH = achExceptionProcessingType === PROCESSING_TYPES.CREATE

    if (isCreatingCheck && isCreatingACH) {
      form.setValue('serviceTypeParameter', 'Both')
    } else if (isCreatingCheck && !isCreatingACH) {
      // check only
      form.setValue('serviceTypeParameter', 'Check')
    } else if (!isCreatingCheck && isCreatingACH) {
      // ACH only
      form.setValue('serviceTypeParameter', 'ACH')
    } else {
      form.setValue('serviceTypeParameter', '')
    }
  }, [achExceptionProcessingType, checkExceptionProcessingType, form, t])

  // set payee match
  useEffect(() => {
    const isCreatingCheck = checkExceptionProcessingType === PROCESSING_TYPES.CREATE
    const isCreatingACH = achExceptionProcessingType === PROCESSING_TYPES.CREATE
    const isCheckDirty = form.getFieldState('checkExceptionProcessingType').isDirty
    const isAchDirty = form.getFieldState('achExceptionProcessingType').isDirty
    const isValueChanged = isCheckDirty || isAchDirty

    if (isValueChanged) {
      if (isCreatingCheck && isCreatingACH) {
        form.setValue('payeeMatch', true)
      } else if (isCreatingCheck && !isCreatingACH) {
        // check only
        form.setValue('payeeMatch', true)
      } else if (!isCreatingCheck && isCreatingACH) {
        // ACH only
        form.setValue('payeeMatch', false)
      } else {
        form.setValue('payeeMatch', false)
      }
    }
  }, [achExceptionProcessingType, checkExceptionProcessingType, form, t])

  // get account detail when loading
  useEffect(() => {
    ;(async () => {
      try {
        if (id) {
          setLoading(true)
          const accountResponse = await getAccountById({
            accountId: id,
          })
          if (accountResponse.statusCode === STATUS_CODES.SUCCESS) {
            handleSetAccount(accountResponse.data)
            handleGetClient(accountResponse.data.clientId)
          }
        }
      } catch (error) {
        console.log('ERROR: ', error)
      } finally {
        setLoading(false)
        setIsFetched(true)
      }
    })()
  }, [id])

  return (
    <ViewAccountContext.Provider
      value={{
        form,
        checkExceptionProcessingType,
        achExceptionProcessingType,
        loading,
        isFetched,
        client,
      }}
    >
      {children}
    </ViewAccountContext.Provider>
  )
}

export default ViewAccountProvider
